import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Customized,
} from "recharts";
import type { SwitchMarker } from "./utils";
import { VariantSwitchMarkerLayer } from "./VariantSwitchMarkerLayer";

interface ChartDataPoint {
  date: string;
  avgRank: number | null;
  unitsSold: number | null;
  bsr: number | null;
}

interface CorrelationChartProps {
  data: ChartDataPoint[];
  markers?: SwitchMarker[];
  onViewTest?: (testName: string) => void;
}

export function CorrelationChart({ data, markers = [], onViewTest }: CorrelationChartProps) {
  const bottomMargin = markers.length > 0 ? 40 : 0;

  return (
    <div className="h-[280px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: bottomMargin }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            className="fill-muted-foreground"
            interval={4}
          />
          <YAxis
            yAxisId="bsr"
            orientation="left"
            reversed
            domain={["auto", "auto"]}
            tick={{ fontSize: 10, fill: "hsl(var(--chart-5))" }}
            width={32}
            tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
            axisLine={{ stroke: "hsl(var(--chart-5))", strokeOpacity: 0.5 }}
            tickLine={{ stroke: "hsl(var(--chart-5))", strokeOpacity: 0.3 }}
          />
          <YAxis
            yAxisId="rank"
            orientation="left"
            reversed
            domain={[1, "auto"]}
            tick={{ fontSize: 10, fill: "hsl(var(--primary))" }}
            width={40}
            axisLine={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.5 }}
            tickLine={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.3 }}
          />
          <YAxis
            yAxisId="sales"
            orientation="right"
            tick={{ fontSize: 11 }}
            className="fill-muted-foreground"
          />
          <RechartsTooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              borderRadius: "8px",
              fontSize: "12px",
            }}
            labelStyle={{ fontWeight: 600, marginBottom: 4 }}
            formatter={(value: number | null, name: string) => {
              if (name === "avgRank") return [value != null ? `#${value}` : "—", "Avg Brand Rank"];
              if (name === "unitsSold") return [value != null ? value : "—", "Units Sold"];
              if (name === "bsr") return [value != null ? `#${(value as number).toLocaleString()}` : "—", "BSR"];
              return [value, name];
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }}
            formatter={(value: string) => {
              const label =
                value === "avgRank" ? "Avg Brand Rank" :
                value === "unitsSold" ? "Units Sold" :
                value === "bsr" ? "BSR" : value;
              return <span style={{ color: "hsl(var(--foreground))" }}>{label}</span>;
            }}
          />
          <Bar
            yAxisId="sales"
            dataKey="unitsSold"
            fill="hsl(var(--primary) / 0.2)"
            radius={[2, 2, 0, 0]}
            barSize={14}
          />
          <Line
            yAxisId="rank"
            type="monotone"
            dataKey="avgRank"
            stroke="hsl(var(--primary))"
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Line
            yAxisId="bsr"
            type="monotone"
            dataKey="bsr"
            stroke="hsl(var(--chart-5))"
            strokeWidth={1.5}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 3 }}
          />
          {markers.length > 0 && (
            <Customized
              component={(props: Record<string, unknown>) => (
                <VariantSwitchMarkerLayer
                  markers={markers}
                  xAxisMap={props.xAxisMap as Record<string, { scale: (v: string) => number | undefined; y: number }>}
                  yAxisMap={props.yAxisMap as Record<string, unknown>}
                  offset={props.offset as { top: number; bottom: number; left: number; right: number }}
                  onViewTest={onViewTest}
                />
              )}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
